import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Alert,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material';
import { PlayArrow, Edit, GitHub } from '@mui/icons-material';
import { renderHtmlContent } from '../../shared/htmlUtils';
import CommentBox from './CommentBox';
import './AIDeveloperComponents.scss';

const TaskPreviewDialog = ({
  taskData,
  selectedRepository,
  setSelectedRepository,
  repositories,
  isEditingDescription,
  setIsEditingDescription,
  setTaskData,
  isDescriptionEmpty,
  handleCancelPreview,
  handleStartPlanning,
  loading
}) => {
  const { t } = useTranslation();
  const { dropdowns } = useSelector((state) => state.tickets);

  return (
    <Paper className="preview-paper">
      <Box className="preview-header">
        <Typography variant="h6" className="preview-title">
          {t('aiTaskPlanner.preview.title')}
        </Typography>
      </Box>
      
      <Alert severity="info" className="preview-alert">
        <Typography variant="body2" className="alert-title">
          {t('aiTaskPlanner.preview.guidance.title')}
        </Typography>
        <Typography variant="body2" className="alert-description">
          {t('aiTaskPlanner.preview.guidance.description')}
        </Typography>
        <Box component="ul" className="alert-list">
          {Array.isArray(t('aiTaskPlanner.preview.guidance.requirements', { returnObjects: true })) && 
            t('aiTaskPlanner.preview.guidance.requirements', { returnObjects: true }).map((req, idx) => (
              <li key={`requirement-${idx}-${req.slice(0, 10)}`}>{req}</li>
            ))
          }
        </Box>
        <Typography variant="caption" className="alert-caption">
          {t('aiTaskPlanner.preview.guidance.caption')}
        </Typography>
      </Alert>
      
      <Box className="preview-content">
        <Box className="field-section">
          <Typography variant="caption" className="field-label">
            {t('aiTaskPlanner.preview.fields.title')}
          </Typography>
          <Box className="field-display">
            <Typography variant="body1" className="field-title">
              {taskData.title}
            </Typography>
          </Box>
        </Box>
        
        <Box className="field-section">
          <Typography variant="caption" className="field-label">
            {t('aiTaskPlanner.preview.fields.repository')}
          </Typography>
          <Select
            fullWidth
            value={selectedRepository}
            onChange={(e) => setSelectedRepository(e.target.value)}
            size="small"
            className="repository-select"
          >
            {repositories.map((repo) => (
              <MenuItem key={repo.key} value={repo.key}>
                <Box className="repository-item">
                  <Typography>{repo.key}</Typography>
                  <Tooltip title={t('aiTaskPlanner.preview.tooltips.viewRepository')} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                    <IconButton
                      component="a"
                      href={repo.value.repo_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                      className="repo-link"
                    >
                      <GitHub sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Box>
        
        <Box className="field-section">
          <Box className="description-header">
            <Typography variant="caption" className="field-label">
              {t('aiTaskPlanner.preview.fields.description')}
            </Typography>
            <IconButton size="small" onClick={() => setIsEditingDescription(!isEditingDescription)} className="edit-button">
              <Edit sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          {isEditingDescription ? (
            <CommentBox
              initialContent={taskData.description}
              onSubmit={(content) => {
                setTaskData(prev => ({ ...prev, description: content }));
                setIsEditingDescription(false);
              }}
              onCancel={() => setIsEditingDescription(false)}
              hideAttachments={true}
              placeholder={t('aiTaskPlanner.preview.placeholders.description')}
              mentionUsers={dropdowns?.assignees || []}
            />
          ) : (
            <Box className={`description-content ${(() => {
              const isEmpty = isDescriptionEmpty(taskData.description);
              return isEmpty ? 'error' : '';
            })()}`}>
              {isDescriptionEmpty(taskData.description) ? (
                <Typography variant="body2" color="text.secondary" style={{ fontStyle: 'italic' }}>
                  {t('aiTaskPlanner.preview.placeholders.descriptionEmpty')}
                </Typography>
              ) : (
                <div className="html-content" dangerouslySetInnerHTML={{ __html: renderHtmlContent(taskData.description) }} />
              )}
            </Box>
          )}
        </Box>
      </Box>
      
      {!isEditingDescription && (
        <Box className="preview-actions">
          <Button variant="outlined" onClick={handleCancelPreview} disabled={loading} className="action-button">
            {t('aiTaskPlanner.preview.actions.cancel')}
          </Button>
          <Button 
            variant="contained" 
            onClick={handleStartPlanning} 
            startIcon={<PlayArrow />} 
            disabled={(() => {
              const isLoading = loading;
              const descEmpty = isDescriptionEmpty(taskData.description);
              const noRepo = !selectedRepository;
              const shouldDisable = isLoading || descEmpty || noRepo;
              return shouldDisable;
            })()} 
            className="action-button primary"
          >
            {loading ? t('aiTaskPlanner.preview.actions.generating') : t('aiTaskPlanner.preview.actions.generate')}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default TaskPreviewDialog;
